/**
 * SEO utility functions for URL structure optimization and metadata generation
 */

/**
 * Creates a URL-friendly slug from a product name
 */
export const createProductSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Creates a SEO-friendly product URL
 */
export const createProductUrl = (id: string, name: string): string => {
  return `/products/${id}/${createProductSlug(name)}`;
};

/**
 * Creates a SEO-friendly creator URL
 */
export const createCreatorUrl = (id: string, name: string): string => {
  return `/creator/${id}/${createProductSlug(name)}`;
};

/**
 * Generates SEO-optimized meta description from product data
 */
export const generateProductMetaDescription = (
  name: string,
  category: string,
  material: string,
  price: string,
  description?: string
): string => {
  const baseDescription = `${name} - Premium 3D printed ${category.toLowerCase()} miniature in ${material}. Starting from £${price}.`;

  if (description) {
    const shortDescription = description
      .substring(0, 100)
      .replace(/<[^>]*>/g, ''); // Strip HTML
    return `${baseDescription} ${shortDescription}...`.substring(0, 160);
  }

  return `${baseDescription} Perfect for tabletop gaming, D&D campaigns, and miniature painting. High-quality 3D printed miniatures from CodedGhost Minis.`.substring(
    0,
    160
  );
};

/**
 * Generates SEO keywords from product data
 */
export const generateProductKeywords = (
  name: string,
  category: string,
  material: string,
  creatorName: string,
  scale?: string
): string => {
  const keywords = [
    name.toLowerCase(),
    category.toLowerCase(),
    material.toLowerCase(),
    '3d printed miniature',
    'tabletop gaming',
    'miniature painting',
    'd&d miniature',
    'warhammer alternative',
    creatorName.toLowerCase(),
    'codedghost minis',
  ];

  if (scale) {
    keywords.push(`${scale} scale`);
  }

  return keywords.join(', ');
};

/**
 * Generates breadcrumb data for product pages
 */
export const generateProductBreadcrumbs = (
  productName: string,
  category: string,
  productId: string
) => {
  return [
    { name: 'Home', url: '/' },
    { name: 'Search', url: '/search' },
    { name: category, url: `/search?category=${encodeURIComponent(category)}` },
    { name: productName, url: createProductUrl(productId, productName) },
  ];
};

/**
 * Generates breadcrumb data for creator pages
 */
export const generateCreatorBreadcrumbs = (
  creatorName: string,
  creatorId: string
) => {
  return [
    { name: 'Home', url: '/' },
    { name: 'Creators', url: '/creators' },
    { name: creatorName, url: createCreatorUrl(creatorId, creatorName) },
  ];
};

/**
 * Generates breadcrumb data for category pages
 */
export const generateCategoryBreadcrumbs = (categoryName: string) => {
  return [
    { name: 'Home', url: '/' },
    { name: 'Search', url: '/search' },
    {
      name: categoryName,
      url: `/search?category=${encodeURIComponent(categoryName)}`,
    },
  ];
};

/**
 * Extracts plain text from HTML content for meta descriptions
 */
export const extractTextFromHtml = (
  html: string,
  maxLength: number = 160
): string => {
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > maxLength
    ? `${text.substring(0, maxLength - 3)}...`
    : text;
};

/**
 * Formats price for display in meta data
 */
export const formatPriceForMeta = (price: number): string => {
  return price.toFixed(2);
};

/**
 * Converts stock status to availability for structured data
 */
export const mapStockStatusToAvailability = (
  stockStatus: string
): 'in stock' | 'out of stock' | 'pre-order' => {
  switch (stockStatus.toLowerCase()) {
    case 'instock':
    case 'in stock':
      return 'in stock';
    case 'preorder':
    case 'pre-order':
      return 'pre-order';
    case 'outofstock':
    case 'out of stock':
    default:
      return 'out of stock';
  }
};

/**
 * Generates canonical URL for a page
 */
export const generateCanonicalUrl = (path: string): string => {
  const baseUrl = 'https://cgminis.co.uk';
  return `${baseUrl}${path}`;
};

/**
 * Validates and formats URLs
 */
export const formatUrl = (url: string): string => {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
};
